// src/App.js

import { lazy, Suspense }   from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { useAuth }          from './context/AuthContext';
import { AuthProvider }     from './context/AuthContext';
import { ProgressProvider } from './context/ProgressContext';
import ScrollToTop          from './components/ScrollToTop';
import ErrorBoundary        from './components/ErrorBoundary';
import PaywallGuard         from './components/PaywallGuard';
import Navbar               from './components/Navbar';
import Footer               from './components/Footer';

// ── Static — small files, loaded immediately ──────────────────────────────────
import Auth           from './pages/Auth';
import NotFound       from './pages/NotFound';
import PaymentSuccess from './pages/PaymentSuccess';
import ComingSoon     from './pages/ComingSoon';

// ── Lazy — larger pages, loaded only when needed ──────────────────────────────
const Home                = lazy(() => import('./pages/Home'));
const Pricing             = lazy(() => import('./pages/Pricing'));
const Dashboard           = lazy(() => import('./pages/Dashboard'));
const Modules             = lazy(() => import('./pages/Modules'));
const Lesson              = lazy(() => import('./pages/Lesson'));
const Tools               = lazy(() => import('./pages/Tools'));
const Glossary            = lazy(() => import('./pages/Glossary'));
const Account             = lazy(() => import('./pages/Account'));
const LandingCandlesticks = lazy(() => import('./pages/LandingCandlesticks'));
const Blog                = lazy(() => import('./pages/Blog'));
const BlogPost            = lazy(() => import('./pages/BlogPost'));
const Disclaimer          = lazy(() => import('./pages/Disclaimer'));
const Terms               = lazy(() => import('./pages/Terms'));
const Privacy             = lazy(() => import('./pages/Privacy'));
const Refund              = lazy(() => import('./pages/Refund'));

// ── Layout ────────────────────────────────────────────────────────────────────
function WithNav() {
  const { loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a' }} />
  );
  return (
    <Suspense fallback={
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a' }} />
    }>
      <Navbar />
      <Outlet />
      <Footer />
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProgressProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>

              <Route element={<WithNav />}>

                {/* ── LIVE ── */}
                <Route path="/"          element={<Home />}           />
                <Route path="/modules"   element={<Modules />}        />
                <Route path="/pricing"   element={<Pricing />}        />
                <Route path="/glossary"  element={<Glossary />}       />
                <Route path="/dashboard" element={<Dashboard />}      />
                <Route path="/account"   element={<Account />}        />
                <Route path="/success"   element={<PaymentSuccess />} />

                {/* SEO landing page */}
                <Route path="/learn/candlestick-charts" element={<LandingCandlesticks />} />

                {/* Lessons with paywall */}
                <Route path="/learn/:moduleId/:lessonId"
                  element={<PaywallGuard><Lesson /></PaywallGuard>}
                />

                {/* ── TOOLS & COMING SOON ── */}
                <Route path="/tools"     element={<Tools />}      />
                <Route path="/markets"   element={<ComingSoon />} />
                <Route path="/technical" element={<ComingSoon />} />
                <Route path="/watchlist" element={<ComingSoon />} />
                <Route path="/backtest"  element={<ComingSoon />} />
                <Route path="/brokers"   element={<ComingSoon />} />

                {/* ── LEGAL ── */}
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/terms"      element={<Terms />}      />
                <Route path="/privacy"    element={<Privacy />}    />
                <Route path="/refund"     element={<Refund />}     />

                {/* ── BLOG ── */}
                <Route path="/blog"          element={<Blog />}     />
                <Route path="/blog/:postId"  element={<BlogPost />} />

              </Route>

              {/* ── Auth — no navbar ── */}
              <Route path="/login"  element={<Auth defaultTab="login"  />} />
              <Route path="/signup" element={<Auth defaultTab="signup" />} />

              {/* ── 404 ── */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*"    element={<NotFound />} />

            </Routes>
          </BrowserRouter>
        </ProgressProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}